import type {
	WhiteningOrder,
	WhiteningOrderKind,
	WhiteningShipTo,
	WhiteningSummary,
} from '@/api/types/PatientApi'
import type { RowStatus } from '@/components/Rows'
import type { GlyphName } from '@/components/svgs/Glyph'
import type { LedgerItem } from '@/ui/core/ledger'
import { format } from 'date-fns'

/**
 * The whitening kit as two cards, derived from the one summary the server sends.
 *
 * Pure, and the only place the kit's words are written: the tile, both cards and both sheets
 * read the same object, so a member who sees "Kit On Its Way" on Home cannot meet a different
 * sentence for the same state one tap later. The states are the server's — we do not infer a
 * stage from a status, or a claim from a local flag, as the device-side store this replaced had
 * to.
 *
 * Nothing here touches React, so it runs under jest as is.
 */

/**
 * What is in the box. The contents in every state, from the claim to long after it has arrived:
 * the card is the kit, and what the kit is does not change because it happens to be in a van.
 *
 * Item 03 is where the instructions live, and it is the only place they need to. A printed
 * step-by-step guide ships in the kit, so it is already in the member's hand at the exact moment
 * they are holding the trays — which is why an in-app copy of it, behind a link, never felt like
 * part of this card: it was not the card's job. The line says where the paper is and who to ask
 * when the paper is not enough, and that is the whole of it.
 */
export const KIT_CONTENTS: readonly LedgerItem[] = [
	{ title: 'Custom-fit whitening trays', line: 'Made from your 3D scan' },
	{ title: 'Professional-grade gel', line: 'A four-week supply' },
	{
		title: 'Step-by-step care guide',
		line: 'In the box, and your Care Team is a message away',
	},
]

/**
 * The kit card's eyebrow and title never change.
 *
 * They are what the card is, not where it has got to — and the card is the same object in every
 * state. The pill carries the state, which is one place rather than three, and it means the type
 * over the photograph is set once and never reflows.
 */
const KIT_EYEBROW = 'Included with your membership'
const KIT_TITLE = 'Your Whitening Kit'

const GEL_EYEBROW = 'Refill'
const GEL_TITLE = 'Whitening Gel'

/**
 * The gel card's description, and the same sentence in every one of its states.
 *
 * A card whose one line of copy changes with each status is a card that explains the rule at the
 * moment the rule bites and never again — so the member who reads "posted to the same address"
 * while ordering has been told nothing about why the next one waits, and the member counting
 * down is being told the reason as though it were news. Said once, permanently, it is the thing
 * the card is *for*: the state itself is the pill's job and the detail is the slot's.
 *
 * A clinical limit, not a stock one, which is the whole reason it is worth a sentence at all.
 */
const GEL_RULE =
	'We space refills four weeks apart. Whitening more often than that can make teeth sensitive to hot and cold.'

/** The bare word a card uses for a delivery. The tile says which parcel; see `tilePill`. */
const DELIVERED = 'Delivered'

/**
 * What the refill button says, live or dormant.
 *
 * One string, because the dormant one only works if it is the same shape and the same words as
 * the button it will become: a member who has met it inactive should recognise the thing they
 * are pressing when it wakes up.
 */
const REQUEST_GEL = 'Request More Gel'

/**
 * The pill, top right of either card. Seven words on the cards; the tile spends eight, because
 * it qualifies the one that is ambiguous out of context. See `tilePill`.
 */
export type WhiteningPill = {
	label: string
	/** Picks the mark: the clock for what is waiting, the check for what is done. */
	status: RowStatus
	/** Overrides that mark. The two "ready" states wear a star; neither waits nor is done. */
	glyph?: GlyphName
}

/** What a card's one action does, where it has one. */
export type WhiteningActionId = 'claim-kit' | 'order-gel'

/**
 * The one line under the delivered kit's slot, with its last words pressable.
 *
 * Two inline links stood here — one to an in-app copy of the printed guide, one to chat — and
 * the guide was not the card's job, so what is left is a single sentence offering the one thing
 * the app can actually do that the box cannot.
 */
export type WhiteningFootnote = { text: string; link: string }

/**
 * The one slot at a card's foot. Always the same footprint, whichever of these fills it, so a
 * card never changes shape as its state moves.
 *
 * `note` is an unpressable row — the claim's date, or a parcel we have no number for.
 * `tracking` is the parcel. On the kit it stays after delivery on purpose: the day tracking says
 * delivered and the porch is empty, the number is the only thing a member actually needs.
 * `dormant` is the library's inactive treatment, a button drawn with its own date on its face
 * rather than hidden until the day it works.
 *
 * One slot, so on any card with two facts to show the slot holds whichever of them cannot be
 * read off the pill. That trade only arises on the gel card; see `gelCard`.
 */
export type WhiteningSlot =
	| { kind: 'button'; action: WhiteningActionId; label: string }
	| { kind: 'dormant'; label: string }
	/**
	 * The mark in the row's disc, which a note has to be told because a note is two different
	 * things: the claim's date, which is a thing waiting and wears the clock, and a parcel we
	 * know shipped but have no number for, which is a thing moving and wears the van. The
	 * tracking row is not told — it is a parcel by definition, and a field that can only hold
	 * one value is not a decision.
	 */
	| { kind: 'note'; label: string; glyph: GlyphName }
	| { kind: 'tracking'; label: string; url?: string }

export type WhiteningCard = {
	/** Absent only where no settled word names the state. See the gel card's cooling-down case. */
	pill?: WhiteningPill
	eyebrow: string
	title: string
	/** The gel card's one line of copy. The kit card has none: its ledger says what it is. */
	line?: string
	/** Absent only where a card has nothing honest to offer; the footprint is kept regardless. */
	slot?: WhiteningSlot
	/** The kit card's last line, once it is home. The only thing under the slot, ever. */
	footnote?: WhiteningFootnote
}

export type WhiteningView = {
	kit: WhiteningCard
	gel: WhiteningCard
	/**
	 * Which of the two cards Home's tile speaks for.
	 *
	 * Not "whichever has a pill", which is what it used to be. That held only while the gel card
	 * waited for the kit; drawn in every state it can have a pill where nothing is happening to
	 * any gel, and the tile would then speak for a card that has nothing to say. The gel leads
	 * only where there is a refill actually in play — one on its way, one that arrived, or one
	 * that can be ordered now — which is a fact about the summary rather than about what a
	 * corner happens to be wearing.
	 */
	lead: 'kit' | 'gel'
}

export type WhiteningInput = {
	/** Undefined while the read is in flight, or after one that failed. */
	summary?: WhiteningSummary
}

/**
 * A bare `yyyy-MM-dd` is a day, not an instant. Handed straight to `new Date` it becomes
 * midnight UTC and prints as the day before anywhere west of Greenwich, which on a line that
 * says when a refill opens is a whole day of somebody's patience. Taken apart and rebuilt in
 * local time; anything with a time in it is already an instant and is left alone.
 */
const asLocalDay = (value: string) => {
	const bare = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
	return bare
		? new Date(Number(bare[1]), Number(bare[2]) - 1, Number(bare[3]))
		: new Date(value)
}

/**
 * A bare date, as the rest of the app writes one: `MMM d`, which is what a billing instalment's
 * line wears ("Due Sep 1") and what a chat's day label and the picker's week range are set in.
 * `MMM d, yyyy` is the app's other date and belongs to the places where the year is part of the
 * fact — a charge in the history, a form submitted last spring. Nothing on these two cards is
 * more than a few weeks from today, so the year would be noise.
 */
const asBareDate = (value: string) => format(asLocalDay(value), 'MMM d')

/** "UPS 1Z999AA10123456784", or just the number where the carrier went unnamed. */
export const trackingLabel = (
	order?: Pick<WhiteningOrder, 'carrier' | 'trackingNumber'>
) =>
	order?.trackingNumber
		? [order.carrier, order.trackingNumber].filter(Boolean).join(' ')
		: undefined

/**
 * The parcel in the slot.
 *
 * A shipped order without a number is a real state — the warehouse marks it gone before the
 * carrier hands back a label — so the slot says what happened and when instead of standing
 * empty. The link is the server's own: an absent `trackingUrl` means we do not know where that
 * carrier's page is, and the row is drawn unpressable rather than guessing one.
 */
const parcelSlot = (order: WhiteningOrder): WhiteningSlot => {
	const label = trackingLabel(order)
	if (label) return { kind: 'tracking', label, url: order.trackingUrl }
	if (order.stage === 'DELIVERED') {
		return {
			kind: 'note',
			label: order.deliveredAt
				? `Delivered ${asBareDate(order.deliveredAt)}`
				: 'Delivered',
			glyph: 'van',
		}
	}
	return {
		kind: 'note',
		label: order.shippedAt ? `Posted ${asBareDate(order.shippedAt)}` : 'On its way',
		glyph: 'van',
	}
}

/** An order the member is still inside. A cancelled one puts them back where they started. */
const live = (order?: WhiteningOrder) =>
	order && order.stage !== 'CANCELLED' ? order : undefined

/**
 * The kit card, in whichever of its five states it is in.
 *
 * The photograph, the eyebrow, the title and the ledger are the same in all of them; only the
 * pill and the slot move. Which is the point of the restructure: the card is one object a member
 * recognises across weeks, not five layouts that happen to share a photograph.
 */
const kitCard = (summary?: WhiteningSummary): WhiteningCard => {
	const kit = live(summary?.kit)
	const base = { eyebrow: KIT_EYEBROW, title: KIT_TITLE }

	if (kit?.stage === 'AWAITING_VISIT') {
		return {
			...base,
			// Not "After Your Visit": a scan is what the trays are milled from, and a member whose
			// visit happened but whose scan did not would have been told they were waiting on
			// something that had already been and gone.
			pill: { label: 'Ships After Scan', status: 'wait' },
			// The clock, because a claim we have not shipped yet is a thing waiting — which is
			// what that mark means everywhere else in the app.
			slot: {
				kind: 'note',
				label: `Claimed ${asBareDate(kit.requestedAt)}`,
				glyph: 'clock',
			},
		}
	}

	if (kit?.stage === 'BEING_MADE') {
		return {
			...base,
			pill: { label: 'Being Made', status: 'wait' },
			// The clock, because a claim we have not shipped yet is a thing waiting — which is
			// what that mark means everywhere else in the app.
			slot: {
				kind: 'note',
				label: `Claimed ${asBareDate(kit.requestedAt)}`,
				glyph: 'clock',
			},
		}
	}

	if (kit?.stage === 'SHIPPED') {
		// "Kit", because it has a sibling now: a member with a refill in the van needs to know
		// which of the two parcels this line is about.
		return {
			...base,
			pill: { label: 'Kit On Its Way', status: 'wait' },
			slot: parcelSlot(kit),
		}
	}

	if (kit?.stage === 'DELIVERED') {
		return {
			...base,
			pill: { label: DELIVERED, status: 'done' },
			// The tracking row stays, as the receipt. It is what somebody needs on the day the
			// carrier says delivered and the porch is empty.
			slot: parcelSlot(kit),
			// The card ends here: picture, contents, receipt, one line. The guide it used to
			// offer is printed and in the box, which item 03 now says.
			footnote: { text: 'Need another kit?', link: 'Message Us' },
		}
	}

	if (summary?.canOrderKit) {
		return {
			...base,
			// A star, not a clock or a check: the claim is neither waiting nor done, and this is
			// the pair "Refill Ready" wears on the card below.
			pill: { label: 'Ready to Claim', status: 'due', glyph: 'star' },
			slot: { kind: 'button', action: 'claim-kit', label: 'Claim My Kit' },
		}
	}

	// Nothing claimed and nothing to claim: a plan that does not carry a kit, or a summary still
	// in flight. The card still says what the kit is and keeps the slot's height, because an
	// invented sentence in an empty slot is worse than the space it would fill.
	return base
}

/** The dormant button the two cooling-down states share: the date, on the button's own face. */
const refillFrom = (gelAvailableAt?: string): WhiteningSlot | undefined =>
	gelAvailableAt
		? { kind: 'dormant', label: `More Gel From ${asBareDate(gelAvailableAt)}` }
		: undefined

/**
 * The gel card, drawn only once the kit is home.
 *
 * Every state carries the same description, so the division of labour is strict: the pill says
 * the state, the slot says the detail, and the line explains the rule once. Which means the slot
 * is never decorative — it is the only place a date or a number can live on this card.
 *
 * The order of these four is the whole logic. `canOrderGel` is asked first, so a delivered
 * refill whose four weeks have run becomes the offer again rather than staying a receipt — the
 * card has no empty middle, and a member never meets a page that says only what they cannot do.
 */
const gelCard = (summary: WhiteningSummary): WhiteningCard => {
	const gel = live(summary.gel)
	const kitHome = live(summary.kit)?.stage === 'DELIVERED'
	const base = { eyebrow: GEL_EYEBROW, title: GEL_TITLE, line: GEL_RULE }

	if (summary.canOrderGel) {
		return {
			...base,
			pill: { label: 'Refill Ready', status: 'due', glyph: 'star' },
			slot: { kind: 'button', action: 'order-gel', label: REQUEST_GEL },
		}
	}

	if (gel?.stage === 'SHIPPED') {
		return {
			...base,
			pill: { label: 'Gel On Its Way', status: 'wait' },
			slot: parcelSlot(gel),
		}
	}

	if (gel?.stage === 'DELIVERED') {
		// A refill that came, inside the wait for the next one — so the pill says it arrived and
		// the slot says when they may ask again.
		//
		// The slot used to hold this order's tracking row, as the kit card's still does. With the
		// description fixed there is one slot for two facts, and the reorder date is the one that
		// cannot be read anywhere else: a delivered gel's pill already says it came, which is what
		// a receipt for it would have said a second time. The kit keeps its own receipt, where the
		// parcel is a once-ever box rather than a jar that arrives every few weeks. Falls back to
		// the parcel when the server has named no date, so the slot is never empty.
		return {
			...base,
			pill: { label: DELIVERED, status: 'done' },
			slot: refillFrom(summary.gelAvailableAt) ?? parcelSlot(gel),
		}
	}

	// No gel, and no kit yet either: the first four weeks of it are in the box that has not
	// arrived. The card is a heads-up rather than a control — it exists so that refills, and the
	// fact that they are paced, are something a member has already met before the morning they
	// reach for gel that is not there and learn both at once.
	//
	// It says nothing about what the kit contains; item 02 of the list one card up already does,
	// and this card's subject is the refill after that one.
	if (!kitHome) {
		return {
			...base,
			// No pill, because on this card the pill answers one question — where is your refill
			// — and in this state there is no refill to be anywhere. The corner wore "With Your
			// Kit" for a while, which was the only wording that fit it, and it reads as a claim
			// about gel the member already has: true of the product, wrong subject for a card
			// about refills, and a status shape reporting no status. An absent pill is the most
			// accurate thing the corner can do.
			//
			// Not an oversight, then: this state has no pill because there is nothing yet to
			// report, and no settled word names that.
			//
			// The row's height is the disc's — 42 points against a pill's 22 — so the corner
			// emptying changes nothing below it.
			//
			// The live button's own word, in the inactive dress: the shape says what will be
			// possible, which an empty foot could not.
			slot: { kind: 'dormant', label: REQUEST_GEL },
		}
	}

	// Never ordered, and inside the four weeks the kit's own gel covers. No pill: none of the
	// settled words names "not yet", and a clock with no label to explain it would only ask the
	// reader to guess. The button's face carries the date; the line already carries the why.
	return { ...base, slot: refillFrom(summary.gelAvailableAt) }
}

/** The summary as the two cards. */
export const buildWhiteningView = ({ summary }: WhiteningInput): WhiteningView => {
	const blank = summary ?? { canOrderKit: false, canOrderGel: false, scanOnFile: false }
	return {
		kit: kitCard(summary),
		// Both cards, always. The gel card was drawn only once the kit had arrived, on the
		// argument that a card which cannot do anything is noise — but it can tell you something
		// you would otherwise learn late, which beats that: a member should meet refills, and the
		// fact that they are paced, before the day they run out rather than on it.
		gel: gelCard(blank),
		lead: leads(summary),
	}
}

/**
 * Which card the tile speaks for: the gel only where a refill is actually in play.
 *
 * A gel card exists in every state now, so its having a pill no longer means anything is
 * happening to any gel. What does mean it is an order — on its way or arrived — or a refill that
 * can be ordered today. Everything else is the kit's story, including the four weeks after a kit
 * lands, where the tile went on showing "Kit Delivered" before this and still does.
 */
const leads = (summary?: WhiteningSummary): WhiteningView['lead'] =>
	summary?.canOrderGel || live(summary?.gel) ? 'gel' : 'kit'

/**
 * The tile's line on Home: the leading card's pill, and which parcel it is about.
 *
 * Undefined while the summary is in flight, which leaves the tile with no line rather than an
 * invented one.
 *
 * "Delivered" is qualified here and only here. On a card the bare word cannot be ambiguous —
 * the card is either the kit or the gel, and its title says which — but the tile shows one word
 * for a page that holds two parcels, and which box arrived is the whole of what somebody
 * glancing at Home wants to know. It is the same distinction "Kit On Its Way" and "Gel On Its
 * Way" already make; delivery was the one state that had not caught up.
 */
export const tilePill = (view: WhiteningView): WhiteningPill | undefined => {
	const pill = view.lead === 'gel' ? view.gel.pill : view.kit.pill
	if (!pill) return undefined
	if (pill.label !== DELIVERED) return pill
	return { ...pill, label: view.lead === 'gel' ? 'Gel Delivered' : 'Kit Delivered' }
}

/**
 * Whether the tile's mark should be the van rather than the sparkle.
 *
 * The sparkle is whitening as an offer; the van is whitening as a delivery, and it is the same
 * van the slot's rows wear — one mark for one meaning, because a member meeting a van on the
 * tile and a box in the row would reasonably wonder whether they were two different things. The
 * two "ready" states are offers, so they keep the sparkle even where an order exists behind them.
 */
export const tileInTransit = (view: WhiteningView) => {
	const pill = tilePill(view)
	return !!pill && pill.glyph !== 'star'
}

/**
 * The Ship To sheet's copy.
 *
 * Ordering is a confirmation now rather than a page-level form, so the caveat that used to sit
 * under a button on the page sits under the button that actually places the order — which is the
 * only place it has ever been true, because it is a statement about what happens next.
 *
 * Only the kit carries it. Both sentences are about trays being milled from a scan, and a member
 * ordering gel has had their trays for a month; repeating it there would be answering a question
 * nobody asked.
 */
export const orderSheet = (kind: WhiteningOrderKind, scanOnFile: boolean) => ({
	button: kind === 'KIT' ? 'Claim My Kit' : 'Request More Gel',
	caption:
		kind === 'KIT'
			? scanOnFile
				? 'Made from the scan we already have — it ships in a few days.'
				: 'Your first visit comes first — your kit ships right after.'
			: undefined,
})

/**
 * Which address the sheet shows: the live one, and only ever the live one.
 *
 * The sheet confirms an order about to be placed, and `shippingAddress` is where that order will
 * go. An order's own `shipTo` is a snapshot of where an older parcel went, so falling back to it
 * would put a stale address under a button that ships to a different one — the same class of
 * quiet wrongness that took the single `order` field off the summary. Absent means absent, and
 * the sheet asks for an address rather than offering a button that cannot work.
 */
export const shipToFor = (summary?: WhiteningSummary) => summary?.shippingAddress

/** The shipping address on one line, the way every other second line in the app is set. */
export const formatShipTo = (shipTo: WhiteningShipTo) =>
	[shipTo.street1, shipTo.street2, `${shipTo.city}, ${shipTo.state} ${shipTo.postalCode}`]
		.filter(Boolean)
		.join(', ')
